<template>
  <v-container fluid class="pa-0">
    <v-card v-if="!enabled" outlined class="feature-card">
      <div class="feature-hero">
        <div class="feature-shield">
          <v-icon size="64" color="white">mdi-shield-check-outline</v-icon>
        </div>
        <div class="feature-eyebrow">WAREHOUSE SECURITY</div>
        <h1>Protect every invoice before merchandise leaves</h1>
        <p class="feature-lead">
          Invoice Exit Guard gives your door staff a fast, reliable way to
          confirm that the printed invoice still matches your live records.
        </p>
      </div>

      <v-card-text class="feature-content">
        <v-row>
          <v-col v-for="benefit in benefits" :key="benefit.title" cols="12" md="4">
            <div class="benefit-card">
              <v-icon size="34" color="primary">{{ benefit.icon }}</v-icon>
              <div class="benefit-title">{{ benefit.title }}</div>
              <div class="benefit-text">{{ benefit.text }}</div>
            </div>
          </v-col>
        </v-row>

        <div class="how-it-works">
          <div class="section-title">How it protects your warehouse</div>
          <div class="steps">
            <div class="step"><span>1</span><div><strong>Print the invoice</strong><small>A unique QR captures its invoice ID and total.</small></div></div>
            <div class="step"><span>2</span><div><strong>Count and scan at the door</strong><small>Your guard verifies the paper against the live invoice.</small></div></div>
            <div class="step"><span>3</span><div><strong>Approve or stop the exit</strong><small>A green approval locks the invoice; a mismatch shows a large red warning.</small></div></div>
          </div>
        </div>

        <div class="activation-box">
          <div>
            <div class="price"><strong>$25</strong><span>/month</span></div>
            <div class="price-note">Includes guard access links, PIN security, invoice locking, and a complete scan audit.</div>
          </div>
          <v-btn
            x-large
            rounded
            color="primary"
            dark
            :loading="activating"
            @click="activate"
          >
            Enable Invoice Exit Guard
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <v-card v-else outlined class="guard-card">
      <div class="guard-header">
        <div>
          <div class="page-title">Invoice Exit Guard</div>
          <div class="page-subtitle">Manage door guards and review invoice exits</div>
        </div>
        <v-btn rounded color="primary" dark @click="openCreate">
          <v-icon left>mdi-plus</v-icon>Add Guard
        </v-btn>
      </div>
      <v-card-text>
        <v-data-table :headers="headers" :items="guards" :loading="loading">
          <template v-slot:item.active="{ item }"><v-chip small :color="item.active ? 'green lighten-5' : 'grey lighten-3'">{{ item.active ? 'Active' : 'Disabled' }}</v-chip></template>
          <template v-slot:item.link="{ item }">
            <v-btn text small color="primary" @click="copyLink(item)"><v-icon left small>mdi-content-copy</v-icon>Copy Link</v-btn>
            <v-btn icon small color="green darken-1" @click="shareLink(item)"><v-icon small>mdi-share-variant</v-icon></v-btn>
          </template>
          <template v-slot:item.actions="{ item }"><v-btn icon @click="edit(item)"><v-icon>mdi-pencil-outline</v-icon></v-btn><v-btn icon color="red" @click="remove(item)"><v-icon>mdi-delete-outline</v-icon></v-btn></template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-dialog v-model="dialog" persistent max-width="520px">
      <v-card class="guard-dialog-card">
        <div class="dialog-header">
          <div>
            <div class="dialog-title">{{ form.id ? "Edit Exit Guard" : "New Exit Guard" }}</div>
            <div class="dialog-subtitle">
              {{ form.id ? "Update guard access and private-link settings." : "Create secure mobile access for a warehouse guard." }}
            </div>
          </div>
          <v-btn icon small :disabled="saving" @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="pa-4">
          <div class="form-section">
            <div class="section-title">Guard Information</div>

            <div class="field-label">Guard Name</div>
            <v-text-field
              v-model.trim="form.name"
              outlined
              dense
              hide-details
              label="Name"
              prepend-inner-icon="mdi-account-hard-hat-outline"
            />

            <div class="field-label mt-3">Four-Digit PIN</div>
            <v-text-field
              v-model="form.pin"
              outlined
              dense
              inputmode="numeric"
              maxlength="4"
              type="password"
              label="PIN"
              prepend-inner-icon="mdi-lock-outline"
              :hint="form.id ? 'Leave blank to keep the existing PIN.' : 'The guard will enter this PIN on their phone.'"
              persistent-hint
            />

            <div class="field-label mt-3">Description</div>
            <v-textarea
              v-model="form.note"
              outlined
              dense
              hide-details
              auto-grow
              rows="2"
              label="Note (optional)"
            />
          </div>

          <div v-if="form.id" class="form-section mt-4">
            <div class="section-title">Access Settings</div>
            <v-switch
              v-model="form.active"
              class="mt-0"
              inset
              hide-details
              color="success"
              label="Allow this guard to scan invoice exits"
            />
            <v-checkbox
              v-model="form.regenerateAccessCode"
              class="mt-4"
              hide-details
              color="primary"
              label="Generate a new private link when saved"
            />
            <div v-if="form.regenerateAccessCode" class="access-warning mt-3">
              <v-icon small color="orange darken-2">mdi-alert-outline</v-icon>
              The guard's existing link will stop working immediately.
            </div>
          </div>
        </v-card-text>

        <v-divider />
        <v-card-actions class="dialog-actions">
          <v-spacer />
          <v-btn text color="primary" :disabled="saving" @click="dialog = false">Close</v-btn>
          <v-btn color="primary" depressed rounded dark :loading="saving" @click="save">
            {{ form.id ? "Update Guard" : "Save Guard" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import copy from "copy-to-clipboard";
import { mapActions, mapGetters } from "vuex";
import settingService from "@/modules/Setting/service";
import service from "../service";

export default {
  data: () => ({
    loading: false,
    saving: false,
    activating: false,
    dialog: false,
    guards: [],
    form: {},
    benefits: [
      { icon: "mdi-cash-lock", title: "Prevent invoice manipulation", text: "Catch changes to invoice totals before products leave your building." },
      { icon: "mdi-qrcode-scan", title: "Simple guard scanning", text: "Give each guard a private mobile link and four-digit PIN—no employee account required." },
      { icon: "mdi-clipboard-text-clock-outline", title: "Complete exit history", text: "See who scanned each invoice, when it exited, and every mismatch attempt." },
    ],
    headers: [
      { text: "Guard", value: "name" },
      { text: "Status", value: "active" },
      { text: "Private Link", value: "link", sortable: false },
      { text: "", value: "actions", sortable: false },
    ],
  }),
  computed: {
    ...mapGetters("global", ["currentBusiness"]),
    enabled() {
      return Boolean(this.currentBusiness && this.currentBusiness.metadata && this.currentBusiness.metadata.invoiceExitGuardEnabled);
    },
  },
  created() {
    if (this.enabled) this.load();
  },
  methods: {
    ...mapActions("global", ["initBusiness"]),
    rows(response) {
      return (response && response.data && response.data.rows) || response.rows || [];
    },
    async activate() {
      const confirmation = await this.$swal({
        title: "Enable Invoice Exit Guard?",
        text: "Your $25 monthly subscription starts when you confirm.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Enable for $25/month",
      });
      if (!confirmation.value) return;

      this.activating = true;
      try {
        const payload = {
          ...this.currentBusiness,
          metadata: {
            ...(this.currentBusiness.metadata || {}),
            invoiceExitGuardEnabled: true,
          },
        };
        const response = await settingService.update(this.currentBusiness.id, payload);
        if (response && response.status === false) throw new Error(response.message || "Unable to activate this feature.");
        this.initBusiness(payload);
        await this.load();
        this.$swal("Invoice Exit Guard Enabled", "You can now add your first guard and create a private scanning link.", "success");
      } catch (error) {
        this.$swal("Invoice Exit Guard", (error.data && error.data.message) || error.message || "Unable to activate this feature.", "error");
      } finally {
        this.activating = false;
      }
    },
    async load() {
      this.loading = true;
      try {
        const guardResponse = await service.getAll();
        this.guards = this.rows(guardResponse);
      } finally {
        this.loading = false;
      }
    },
    openCreate() {
      this.form = { name: "", pin: "", note: "", active: true };
      this.dialog = true;
    },
    edit(item) {
      this.form = { ...item, pin: "", regenerateAccessCode: false };
      this.dialog = true;
    },
    async save() {
      if (!this.form.name || (!this.form.id && !/^\d{4}$/.test(this.form.pin)) || (this.form.pin && !/^\d{4}$/.test(this.form.pin))) {
        return this.$swal("PIN", "Enter exactly four digits.", "error");
      }
      this.saving = true;
      try {
        if (this.form.id) await service.update(this.form.id, this.form);
        else await service.create(this.form);
        this.dialog = false;
        await this.load();
      } catch (error) {
        this.$swal("Exit Guard", (error.data && error.data.message) || "Unable to save guard.", "error");
      } finally {
        this.saving = false;
      }
    },
    copyLink(item) {
      copy(service.link(item.accessCode));
      this.$swal({ toast: true, position: "top-end", timer: 1800, showConfirmButton: false, icon: "success", title: "Guard link copied" });
    },
    async shareLink(item) {
      const url = service.link(item.accessCode);
      if (navigator.share) {
        await navigator.share({ title: "Invoice Exit Guard", text: `${item.name}'s private guard link`, url });
        return;
      }
      this.copyLink(item);
    },
    async remove(item) {
      const confirmation = await this.$swal({ title: `Delete ${item.name}?`, icon: "warning", showCancelButton: true });
      if (confirmation.value) {
        await service.remove(item.id);
        await this.load();
      }
    },
  },
};
</script>

<style scoped>
.guard-card,.feature-card{border-radius:20px;overflow:hidden}.guard-header{padding:20px;display:flex;justify-content:space-between;align-items:center;border-bottom:1px solid #e2e8f0}.page-title{font-size:24px;font-weight:900;color:#0f172a}.page-subtitle{color:#64748b}.section-title{font-size:19px;font-weight:900;color:#0f172a}.feature-hero{padding:44px 24px 34px;text-align:center;background:linear-gradient(135deg,#eff6ff,#f8fafc)}.feature-shield{width:106px;height:106px;border-radius:32px;margin:0 auto 20px;background:linear-gradient(135deg,#2563eb,#0f766e);display:flex;align-items:center;justify-content:center;box-shadow:0 18px 45px rgba(37,99,235,.22)}.feature-eyebrow{color:#2563eb;font-size:12px;font-weight:900;letter-spacing:.14em}.feature-hero h1{max-width:760px;margin:8px auto 12px;color:#0f172a;font-size:34px;line-height:1.2}.feature-lead{max-width:700px;margin:auto;color:#64748b;font-size:16px;line-height:1.65}.feature-content{padding:28px!important}.benefit-card{height:100%;padding:22px;border:1px solid #e2e8f0;border-radius:18px;background:#fff}.benefit-title{margin-top:13px;color:#0f172a;font-size:16px;font-weight:900}.benefit-text{margin-top:6px;color:#64748b;line-height:1.55}.how-it-works{margin-top:24px;padding:24px;border-radius:18px;background:#f8fafc}.steps{display:grid;grid-template-columns:repeat(3,1fr);gap:18px;margin-top:18px}.step{display:flex;gap:12px}.step>span{width:34px;height:34px;flex:0 0 34px;border-radius:11px;background:#2563eb;color:white;font-weight:900;display:flex;align-items:center;justify-content:center}.step div{display:flex;flex-direction:column}.step small{color:#64748b;margin-top:4px;line-height:1.45}.activation-box{margin-top:24px;padding:24px;border:1px solid #bfdbfe;border-radius:18px;background:#eff6ff;display:flex;align-items:center;justify-content:space-between;gap:20px}.price strong{color:#0f172a;font-size:34px}.price span{color:#64748b}.price-note{max-width:620px;color:#475569;font-size:13px}.guard-dialog-card{border-radius:20px;overflow:hidden}.dialog-header{padding:18px 20px;border-bottom:1px solid #e2e8f0;display:flex;align-items:center;justify-content:space-between;gap:16px}.dialog-title{font-size:20px;font-weight:800;color:#0f172a}.dialog-subtitle{font-size:13px;color:#64748b;margin-top:2px}.form-section{background:#f8fafc;border:1px solid #e2e8f0;border-radius:16px;padding:14px}.form-section .section-title{font-size:14px;margin-bottom:12px}.field-label{font-size:12px;font-weight:700;color:#475569;margin-bottom:6px}.dialog-actions{padding:12px 16px}.access-warning{padding:9px 11px;border:1px solid #fed7aa;border-radius:12px;background:#fff7ed;color:#9a3412;font-size:12px;display:flex;align-items:center;gap:7px}@media(max-width:760px){.feature-hero h1{font-size:27px}.steps{grid-template-columns:1fr}.activation-box{align-items:stretch;flex-direction:column}.guard-header{align-items:flex-start;gap:12px;flex-direction:column}.dialog-header{padding:14px 16px}.dialog-title{font-size:18px}}
</style>
